import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {produce} from 'immer'
import {useCallback, useMemo, useState} from 'react'

import type {IssueComment} from '../../page-data/payloads/issue-comments'
import {
  type CommentsFilterState,
  getDefaultFilterState,
  parseFilterKeywords,
  useFilteredThreadIds,
  useFilterIssueCommentsIds,
} from './CommentsFilter'

export interface CommentsFilterOverrides {
  selectedAuthor?: string
  hideResolved?: boolean
  filterText?: string
}

export interface UseCommentsFiltersOptions {
  /** Thread previews the review filter is applied to. */
  threadPreviews: Thread[]
  /** Issue comments and review summaries the conversation filter is applied to. */
  conversationComments: IssueComment[]
}

export interface CommentsFilters {
  threadPreviewFilterState: CommentsFilterState
  setThreadPreviewFilterState: React.Dispatch<React.SetStateAction<CommentsFilterState>>
  issueCommentsFilterState: CommentsFilterState
  setIssueCommentsFilterState: React.Dispatch<React.SetStateAction<CommentsFilterState>>
  /** The set of thread IDs matching the review filter. */
  filteredThreadIds: Set<string>
  /** The set of conversation comment IDs matching the conversation filter. */
  filteredIssueCommentIds: Set<string>
  /** The review ID currently being filtered on, if any. */
  selectedReviewId: number | null
  /** Merges partial filter values into the current filter state. */
  applyFilterOverrides: (overrides: CommentsFilterOverrides) => void
}

/**
 * Owns the review and conversation filter state and derives the matching comment IDs.
 *
 * Review and conversation each keep their own filter state, so this must be owned by the
 * component that renders both the filter controls and the filtered comments.
 */
export function useCommentsFilters({threadPreviews, conversationComments}: UseCommentsFiltersOptions): CommentsFilters {
  const [threadPreviewFilterState, setThreadPreviewFilterState] = useState<CommentsFilterState>(getDefaultFilterState)
  const [issueCommentsFilterState, setIssueCommentsFilterState] = useState<CommentsFilterState>(getDefaultFilterState)

  const filteredThreadIds = useFilteredThreadIds(threadPreviews, threadPreviewFilterState)
  const filteredIssueCommentIds = useFilterIssueCommentsIds(conversationComments, issueCommentsFilterState)

  // Extract the selected review ID from filter state
  const selectedReviewId = useMemo(() => {
    if (!threadPreviewFilterState.filterText) return null
    const {reviewId} = parseFilterKeywords(threadPreviewFilterState.filterText)
    return reviewId
  }, [threadPreviewFilterState.filterText])

  const applyFilterOverrides = useCallback(({selectedAuthor, hideResolved, filterText}: CommentsFilterOverrides) => {
    if (!selectedAuthor && hideResolved === undefined && !filterText) return

    setThreadPreviewFilterState(prev =>
      produce(prev, draft => {
        if (selectedAuthor) draft.selectedAuthor = selectedAuthor
        if (hideResolved !== undefined) draft.hideResolvedThreads = hideResolved
        if (filterText) draft.filterText = filterText
      }),
    )

    // Apply filterText to issue comments filter state as well
    if (filterText) {
      setIssueCommentsFilterState(prev =>
        produce(prev, draft => {
          draft.filterText = filterText
        }),
      )
    }
  }, [])

  return {
    threadPreviewFilterState,
    setThreadPreviewFilterState,
    issueCommentsFilterState,
    setIssueCommentsFilterState,
    filteredThreadIds,
    filteredIssueCommentIds,
    selectedReviewId,
    applyFilterOverrides,
  }
}
